import axios from "axios";
import { getApiBaseUrl } from "@/config/apiBase";
import {
  requestTransitionNote,
  requestTransitionFollowUp,
} from "@/lib/transitionNoteGate";
import { childEndpoints, leadsEndpoints } from "@/config/endpoints";
import { toast } from "sonner";
import { getDetachedToken, isDetachedPage } from "@/lib/detachedPage";
import { ALLOW_STALE_HEADER, noteResponse, wantsStaleAllowed } from "@/lib/staleRefetch";

const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    // 1. Add Token — a page outside the CRM (the live board) sends its OWN
    // sign-in, never the CRM's (lib/detachedPage.ts).
    const token = isDetachedPage()
      ? getDetachedToken()
      : localStorage.getItem("xale_access_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // 2. [CRITICAL FIX] Handle File Uploads
    // If data is FormData, delete the default Content-Type.
    // This lets the browser set "multipart/form-data; boundary=..." automatically.
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    // 3. Dashboard widgets may be answered from their last build while the
    // server rebuilds them (lib/staleRefetch.ts).
    if (wantsStaleAllowed(config.method, config.url)) {
      config.headers[ALLOW_STALE_HEADER] = "1";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// API guard (server `apiGuard` middleware): a user who exceeds the
// per-minute request limit or the daily row budget gets 429 + Retry-After.
// React Query already refuses to retry 4xx, so nothing storms; this only
// tells the user WHY the page stopped loading, once per window rather than
// once per blocked request (a scrolling list can fire a dozen at once).
let lastRateLimitToastAt = 0;
function notifyRateLimited(response: any) {
  const now = Date.now();
  if (now - lastRateLimitToastAt < 15_000) return;
  lastRateLimitToastAt = now;
  const retryAfter = Number(response?.headers?.["retry-after"]);
  const why =
    response?.data?.message ||
    "You're loading data faster than allowed. Please slow down.";
  const when =
    Number.isFinite(retryAfter) && retryAfter > 0
      ? retryAfter >= 3600
        ? ` Try again in about ${Math.ceil(retryAfter / 3600)} hour(s).`
        : ` Try again in about ${Math.ceil(retryAfter / 60)} minute(s).`
      : "";
  toast.error(`${why}${when}`, { id: "api-rate-limit", duration: 8000 });
}

axiosInstance.interceptors.response.use(
  (response) => {
    noteResponse(response.headers?.["x-xale-stale"] === "1");
    return response;
  },
  async (error) => {
    noteResponse(false);
    if (error?.response?.status === 429) {
      notifyRateLimited(error.response);
      return Promise.reject(error);
    }

    // "Notes" requirement gate: a stage/status move blocked ONLY by a missing
    // note comes back as 400 + `data.noteRequired`. Hold the caller's promise,
    // ask the user for a note (global TransitionNoteHost dialog), and replay
    // the exact same request with `note` added to the body. Cancelling (or no
    // mounted host) falls through to the normal rejection, so every call
    // site's own error handling still applies. `__noteRetried` guards loops.
    const cfg: any = error?.config;
    const noteRequired =
      error?.response?.status === 400 &&
      error?.response?.data?.data?.noteRequired === true;

    if (noteRequired && cfg && !cfg.__noteRetried) {
      try {
        // The gate's OWN wording — when a note AND a follow-up are both
        // missing, `message` is whichever failed first.
        const note = await requestTransitionNote(
          error.response.data?.data?.noteMessage ||
            error.response.data?.message ||
            "A note is required to make this change."
        );
        if (note) {
          let body: any = cfg.data;
          if (typeof body === "string") {
            try {
              body = JSON.parse(body);
            } catch {
              body = undefined;
            }
          }
          if (body === undefined || body === null) body = {};
          if (typeof body === "object" && !(body instanceof FormData)) {
            return axiosInstance.request({
              ...cfg,
              __noteRetried: true,
              data: { ...body, note },
            });
          }
        }
      } catch {
        // Fall through to the original rejection.
      }
    }

    // "FollowUps" requirement gate: same shape as the note gate, but the
    // follow-up is a REAL row with a role slot — so instead of smuggling a
    // date through the move payload (which would make the transition a second
    // follow-up writer, bypassing slot resolution and the supersede rule), we
    // write it through the canonical follow-up endpoint and then replay the
    // move. The server names the target entity in `followUpTarget`, because
    // only the validator knows whether the move was on a lead or a deal.
    const followUpRequired =
      error?.response?.status === 400 &&
      error?.response?.data?.data?.followUpRequired === true;
    const followUpTarget = error?.response?.data?.data?.followUpTarget;

    if (
      followUpRequired &&
      cfg &&
      !cfg.__followUpRetried &&
      followUpTarget?.entityId
    ) {
      try {
        const gateData = error.response.data?.data ?? {};
        const iso = await requestTransitionFollowUp(
          gateData.followUpMessage ||
            error.response.data?.message ||
            "A follow-up is required to make this change.",
          {
            // The TARGET bucket's "Maximum days allowed" (the status being
            // moved to) — the write below is judged by the same bucket, so
            // the calendar greys out exactly the days the server rejects.
            maxDays:
              gateData.followUpMaxDays == null
                ? null
                : Number(gateData.followUpMaxDays),
          },
        );
        if (iso) {
          const base =
            followUpTarget.entityType === "child"
              ? childEndpoints
              : leadsEndpoints;
          // The canonical writer: role-slot resolution, supersede of the
          // previous row, activity + cache bust all happen server-side.
          try {
            await axiosInstance.patch(`${base}/${followUpTarget.entityId}`, {
              type: "updateFollowup",
              followUp: {
                date: iso,
                status: "Pending",
                completedBy: {},
                // Echo the move's target bucket so the server applies THAT
                // status's cap (the follow-up is for where the lead is
                // going, not where it is).
                ...(gateData.followUpTargetBucket
                  ? { transitionTarget: gateData.followUpTargetBucket }
                  : {}),
              },
            });
          } catch (writeErr: any) {
            // The follow-up could not be saved (typically: no assignee whose
            // role does follow-ups in the CURRENT stage). Say so — a silent
            // fall-through looked like "I scheduled it and it still won't
            // move" — then let the original rejection stand.
            const why =
              writeErr?.response?.data?.message ||
              "The follow-up could not be saved, so the move was not made.";
            toast.error(`Couldn't schedule the follow-up: ${why}`);
            return Promise.reject(error);
          }
          // This write bypasses patchLeadSilently / patchChildSilently, so
          // their onSuccess never refreshes the dashboard's follow-up rail —
          // the lead stayed in Overdue on screen after the popup rescheduled
          // it (2026-09-12). Lazy imports: queryClient imports this module.
          try {
            const [{ queryClient }, { invalidateFollowUpDashboardQueries }] =
              await Promise.all([
                import("@/lib/queryClient"),
                import("@/features/Dashboard/api/invalidateFollowUpDashboardQueries"),
              ]);
            invalidateFollowUpDashboardQueries(queryClient);
          } catch {
            // Advisory refresh only — never let it interfere with the move.
          }
          return axiosInstance.request({ ...cfg, __followUpRetried: true });
        }
      } catch {
        // Fall through to the original rejection — the move never happened,
        // so nothing is left half-applied.
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
